// @ts-nocheck
function dynamicKey(key, dataLength) {
  // Repeat the key and slice to match the length of the data
  return key.repeat(Math.ceil(dataLength / key.length)).slice(0, dataLength);
}

function affineEncrypt(data, key) {
  const a = 5; // Choose a value for a (should be coprime to 26)
  const b = 8; // Choose a value for b
  let encrypted = '';

  for (let char of data) {
    if (/[a-zA-Z]/.test(char)) {
      // Check if the character is a letter
      const base = char.charCodeAt(0) < 97 ? 65 : 97; // Upper or lower case
      const encryptedChar = String.fromCharCode(
        ((a * (char.charCodeAt(0) - base) + b) % 26) + base,
      );
      encrypted += encryptedChar;
    } else {
      encrypted += char; // Non-letter characters remain unchanged
    }
  }
  return encrypted;
}

function vigenereEncrypt(data, key) {
  const repeatedKey = key
    .repeat(Math.ceil(data.length / key.length))
    .slice(0, data.length);
  let encrypted = '';

  for (let i = 0; i < data.length; i++) {
    const char = data[i];
    const keyChar = repeatedKey[i];

    if (/[a-zA-Z]/.test(char)) {
      const base = char.charCodeAt(0) < 97 ? 65 : 97;
      const offset =
        keyChar.charCodeAt(0) < 97
          ? keyChar.charCodeAt(0) - 65
          : keyChar.charCodeAt(0) - 97;
      const encryptedChar = String.fromCharCode(
        ((char.charCodeAt(0) - base + offset) % 26) + base,
      );
      encrypted += encryptedChar;
    } else {
      encrypted += char; // Non-letter characters remain unchanged
    }
  }
  return encrypted;
}

function xorEncrypt(data, key) {
  const dynamicKeyUsed = dynamicKey(key, data.length);
  let encrypted = '';

  for (let i = 0; i < data.length; i++) {
    encrypted += String.fromCharCode(
      data.charCodeAt(i) ^ dynamicKeyUsed.charCodeAt(i),
    );
  }
  return encrypted;
}

function encrypt(data, key) {
  // Ensure data is a string
  if (!key || !key.length) {
    return 'Key is required';
  }
  if (typeof data !== 'string') {
    data = JSON.stringify(data); // Convert to string if it's not
  }

  const dynamicKeyUsed = dynamicKey(key, data.length);
  let encryptedData = affineEncrypt(data, dynamicKeyUsed);
  encryptedData = vigenereEncrypt(encryptedData, dynamicKeyUsed);
  encryptedData = xorEncrypt(encryptedData, dynamicKeyUsed);
  return encryptedData;
}

class Web3Provider extends HTMLElement {
  constructor() {
    super();
    document.addEventListener(
      'product-burned',
      this.onProductBurned.bind(this),
    );
    localStorage.setItem('walletConnected', false);
  }

  connectedCallback() {
    try {
      this.bindEvents();
    } catch (error) {
      console.error('An error occurred:', error);
    }
  }

  bindEvents() {
    try {
      document.addEventListener(
        'wallet-connected',
        this.onWalletConnect.bind(this),
      );
      document.addEventListener(
        'wallet-disconnected',
        this.onWalletDisconnect.bind(this),
      );
      this.removeProductGate();
    } catch (error) {
      console.error('error on updating real time:', error);
    }
  }

  async onWalletConnect() {
    localStorage.setItem('walletConnected', true);
    await this.productGateInPDP('connect');
    await this.burnNFT();
    await this.handlePricing('connect');
    await this.updateStrings('connect');
  }

  async onWalletDisconnect() {
    // console.log('Wallet disconnected function called');
    localStorage.setItem('walletConnected', false);
    await this.productGateInPDP('disconnect');
    await this.burnNFT();
    await this.handlePricing('disconnect');
    await this.updateStrings('disconnect');
  }

  async findAndCheckNFT(nfts, requiredCollections, requiredTraits, gateType) {
    try {
      // Split the comma-separated collection addresses
      const requiredCollectionArray = requiredCollections
        .split(',')
        .map((c) => c.trim());

      // Parse requiredTraits if it's a string
      const requiredTraitsArray = JSON.parse(requiredTraits);

      // Token-gate check
      if (gateType === 'token-gate') {
        return requiredCollectionArray.some((collectionAddress) =>
          nfts.some((nft) => this.isNFTInCollection(nft, collectionAddress)),
        );
      }

      // Trait-gate check
      if (gateType === 'trait-gate') {
        // For each collection and its required traits
        for (const {
          collection: collectionAddress,
          traits: traitsArray,
        } of requiredTraitsArray) {
          // Filter NFTs that belong to the current collection
          const nftsInCollection = nfts.filter((nft) =>
            this.isNFTInCollection(nft, collectionAddress),
          );

          // Check if any of the filtered NFTs match all required traits
          const hasMatchingNFT = nftsInCollection.some((nft) =>
            this.doesNFTMatchTraits(nft, traitsArray),
          );

          if (hasMatchingNFT) {
            return true;
          }
        }

        return false;
      }

      // If we reach here, it means the gateType was neither 'token-gate' nor 'trait-gate'
      return false;
    } catch (error) {
      console.error('Error finding and checking NFT:', error);
      return false;
    }
  }

  isNFTInCollection(nft, collectionAddress) {
    return nft.grouping?.some(
      (group) =>
        group.group_key === 'collection' &&
        group.group_value === collectionAddress,
    );
  }

  doesNFTMatchTraits(nft, traitsArray) {
    if (traitsArray.length === 0) return false;

    return traitsArray.every((traitObj) =>
      Object.entries(traitObj).every(([traitType, traitValue]) =>
        nft.content.metadata.attributes.some(
          (nftAttr) =>
            nftAttr.trait_type.toLowerCase() === traitType.toLowerCase() &&
            nftAttr.value.toLowerCase() === traitValue.toLowerCase(),
        ),
      ),
    );
  }

  async checkBurnCollection(
    walletNfts,
    productBurnCollections,
    burnCollectionAddress,
  ) {
    // Split the comma-separated productBurnCollections into an array
    const requiredCollections = productBurnCollections
      .split(',')
      .map((c) => c.trim());

    // Find the NFTs with the specified collection address.
    // burnCollectionAddress will have an array of collection addresses.
    const targetNFTs = walletNfts.filter(
      (nft) =>
        nft.grouping &&
        nft.grouping.some(
          (group) =>
            group.group_key === 'collection' &&
            burnCollectionAddress.includes(group.group_value),
        ),
    );

    // If no matching NFT found, through an error
    if (targetNFTs.length === 0) {
      console.error('No matching NFT found');
      return null;
    }

    // check if any targetNft is under the required collection.
    const filterNfts = targetNFTs.filter((nft) =>
      nft.grouping.some(
        (group) =>
          group.group_key === 'collection' &&
          requiredCollections.includes(group.group_value),
      ),
    );

    if (filterNfts.length === 0) {
      console.error('No matching NFT found under the required burn collection');
      return null;
    }

    // Parse productNFTs if it's a string
    // const nftAddresses = typeof productNFTs === 'string' ? productNFTs.split('|') : productNFTs;

    // From the targetNfts, find the one that matches the required metafield NFT address.
    // nftAddresses can have multiple addresses separated by '|' or a single string address.
    // const matchingNFT = filterNfts.find(nft => nftAddresses.includes(nft.id));

    // i can burn any NFTs under the required collection.
    const matchingNFT = filterNfts[0];

    return matchingNFT;
  }

  PDPErrorBlock(visibility) {
    const mainProduct = document.querySelector('.bevy-main-product');

    if (!mainProduct) return;

    const solanaConnectBlock = mainProduct.querySelector(
      '.solana-connect-block',
    );
    if (!solanaConnectBlock) return;

    const shouldShowError = visibility === 'show';
    solanaConnectBlock.classList.toggle('show-error', shouldShowError);
  }

  loadingSpinner(visibility) {
    const mainProduct = document.querySelector('.bevy-main-product');

    if (!mainProduct) return;

    const spinnerBlock = mainProduct.querySelector(
      '.bevy-product-info-spinner',
    );
    if (!spinnerBlock) return;

    const shouldShow = visibility === 'hide';
    spinnerBlock.classList.toggle('hidden', shouldShow);
  }

  async onProductBurned(event) {
    console.log('⇐-------------↓↓↓ onProductBurned--> event ↓↓↓-------------⇒');
    console.log(event.detail);
    console.log('⇐-------------↑↑↑ onProductBurned--> event ↑↑↑-------------⇒');

    const productForm = document.querySelector('product-form form');
    if (!productForm) {
      console.error('Product form not found');
      return;
    }

    try {
      const formData = new FormData(productForm);
      formData.set('quantity', 1); // Ensure only 1 quantity is added

      const response = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: {'X-Requested-With': 'XMLHttpRequest'},
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();

      // Redirect to cart page
      window.location.href = window.routes.cart_url;
    } catch (error) {
      console.error('Error in onProductBurned:', error);
    }
  }

  async burnNFT() {
    // first check the wallet connectivity
    // then check the user wallet for the required NFT Collection which is required to burn @window.burnAddresses
    // if the user has the required NFT then show the burn button

    const userWalletNfts = window.userWallet || [];
    const globalBurnCollectionAddress = window.burnAddresses || [];
    const mainProduct = document.querySelector('.bevy-main-product');

    if (!mainProduct || globalBurnCollectionAddress.length === 0) return;

    const productFormBtn = mainProduct?.querySelector('#bevy-buy-button');
    const pdpQuantity = mainProduct?.querySelector('.bevy-quantity__selector');
    const burnBtn = mainProduct?.querySelector('#burn-btn');

    if (localStorage.getItem('walletConnected') === 'false') {
      console.info('burnNFT(): Wallet not connected');
      pdpQuantity.classList.remove('hidden');
      burnBtn.classList.add('hidden');
      this.PDPErrorBlock('hide');
      return;
    }

    try {
      const {productTags, requiredCollection} = mainProduct.dataset;

      // check productTags has any burn tag, which are separated by pipe '|'.
      const tags = productTags.split('|');
      const burnTag = tags.find((tag) => tag === 'burn');

      if (!burnTag || burnTag === 'undefined') return;

      // check if the requiredCollection is empty or not.
      if (!requiredCollection || requiredCollection.trim() === '') {
        console.error('No burn collections specified');
        this.PDPErrorBlock('show');
        return;
      }

      const hasMatchingNFT = await this.checkBurnCollection(
        userWalletNfts,
        requiredCollection,
        globalBurnCollectionAddress,
      );

      if (hasMatchingNFT) {
        console.info('User has the required NFT to Burn');

        pdpQuantity.classList.add('hidden');
        productFormBtn.classList.add('hidden');

        // adding necessary data attributes to the burn button
        const collectionAddress = hasMatchingNFT.grouping.find(
          (group) => group.group_key === 'collection',
        ).group_value;
        burnBtn.dataset.burnNftInterface = hasMatchingNFT.interface;
        burnBtn.dataset.burnNftAddress = hasMatchingNFT.id;
        burnBtn.dataset.burnCollectionAddress = collectionAddress;
        burnBtn.classList.remove('hidden');
      } else {
        console.warn('Does not have required NFT to burn.');
        this.PDPErrorBlock('show');
        throw new Error('User does not have the required NFT to burn.');
      }
    } catch (error) {
      console.error('Error checking burn eligibility: ', error);
    }
  }

  removeProductGate() {
    const mainProduct = document.querySelector('.bevy-main-product');

    if (!mainProduct) return;

    const {productGate, countdownStatus, publicSaleDiscount} =
      mainProduct.dataset;

    if (
      productGate === 'public-gate' ||
      (publicSaleDiscount === 'true' &&
        countdownStatus === 'end' &&
        (productGate === 'token-gate' || productGate === 'trait-gate'))
    ) {
      const productFormBtn = mainProduct.querySelector('#bevy-buy-button');
      const pdpQuantity = mainProduct.querySelector('.bevy-quantity__selector');
      const publicSaleBox = mainProduct.querySelector('.public_sale_block');
      const connectBtn = mainProduct.querySelector('#solana-connect-header');

      if (productFormBtn && pdpQuantity) {
        // before revealing the product form button
        // check is the countdown timer is running or not.
        if (countdownStatus === 'running') {
          pdpQuantity.querySelector('quantity-input').classList.add('disabled');
        } else if (['end', 'neutral'].includes(countdownStatus)) {
          console.info('Countdown is ended/not started.');
          productFormBtn.classList.remove('hidden');
          pdpQuantity
            .querySelector('quantity-input')
            .classList.remove('disabled');
          publicSaleBox?.remove();
        }
      }
      // removing wallet-connect btn from PDP,
      // if product has any discounts then wallet can be connected in the Cart page.
      connectBtn?.remove();
    }
  }

  async productGateInPDP(connectivityStatus) {
    const mainProduct = document.querySelector('.bevy-main-product');
    const userWalletNfts = window.userWallet || [];

    if (mainProduct) {
      const action = connectivityStatus === 'connect' ? 'remove' : 'add';
      const {
        productGate,
        productTags,
        requiredCollection,
        productId,
        publicSaleDiscount,
      } = mainProduct.dataset;
      const productFormBtn = mainProduct.querySelector('#bevy-buy-button');
      // const pdpQuantity = mainProduct.querySelector('.bevy-quantity__selector');
      const solanaConnectBlock = mainProduct.querySelector(
        '.solana-connect-block',
      );

      const tags = productTags.split('|');
      const burnTag = tags.find((tag) => tag === 'burn');

      if (connectivityStatus === 'disconnect') {
        // pdpQuantity.classList[action]('hidden');
        productFormBtn.classList[action]('hidden');
        this.PDPErrorBlock('hide');
        return;
      }

      if (burnTag && burnTag === 'burn') {
        console.info('Product is for Burning.');
        return;
      }

      this.loadingSpinner('show');

      if (productGate === 'public-gate') {
        console.info('Product is for Public Sale.');
        // pdpQuantity?.classList[action]('hidden');
        productFormBtn?.classList[action]('hidden');
      } else if (productGate === 'trait-gate') {
        const requiredTraits = solanaConnectBlock?.dataset.traits;

        const hasMatchingNFT = await this.findAndCheckNFT(
          userWalletNfts,
          requiredCollection,
          requiredTraits,
          productGate,
        );

        this.handleProductBuyButtons(hasMatchingNFT, action, productFormBtn);
      } else if (productGate === 'token-gate') {
        const hasMatchingNFT = await this.findAndCheckNFT(
          userWalletNfts,
          requiredCollection,
          null,
          productGate,
        );
        this.handleProductBuyButtons(hasMatchingNFT, action, productFormBtn);
      } else {
        console.info('Product is for Public Sale.');
      }

      this.loadingSpinner('hide');
    }
  }

  handleProductBuyButtons(hasMatchingNFT, action, productFormBtn) {
    if (hasMatchingNFT) {
      console.info('User has the required NFT');
      productFormBtn?.classList[action]('hidden');
    } else {
      this.PDPErrorBlock('show');
      console.warn('User does not have the required NFT');
    }
  }

  // when we connect wallet in the cart, then this handlePricing() will be executed.

  // async handlePricing(eventType) {
  //   const cartItems = await this.getAllCartItems();
  //   const cartItemProductPriceEls = document.querySelectorAll(
  //     '#main-cart-items product-price',
  //   );
  //   const mainProduct = document.querySelector('.bevy-main-product');

  //   // product id of the main product in the PDP
  //   const mainProductId = mainProduct?.dataset.productId;
  //   const mainProductMetafieldNFT = mainProduct?.dataset.nft;

  //   // Process product-price elements in the cart
  //   for (const productPriceEl of cartItemProductPriceEls) {
  //     const {
  //       productVariantId,
  //       quantity,
  //       requiredTraits,
  //       requiredCollection,
  //       productTags,
  //       productGate,
  //     } = productPriceEl;
  //     const qty = quantity || 1;
  //     const burnTag = productTags.split('|').find((tag) => tag === 'burn'); // return @string "burn"
  //     productPriceEl.discountEligible = false;
  //     productPriceEl.getAndSetPrice();

  //     if (window.location.pathname.startsWith('/cart')) {
  //       // checking in the fetched cartItems, do we have discount_eligibility properties
  //       // check if the product is burn product or not,
  //       // for burn product we hide the product price and give 100% discount if user has any nfts under the required collection
  //       // const properties = cartItems.items.find(
  //       //   (item) => item.id === Number(productVariantId),
  //       // )?.properties;

  //       const wallet_data = {
  //         current_collections: requiredCollection?.split(','),
  //         gate: productGate,
  //         traits_with_collection: JSON.parse(requiredTraits),
  //       };

  //       // check in the properties do we have discount_eligibility
  //       const encryptedData = JSON.stringify(wallet_data) || null;

  //       const property = eventType === 'connect' ? encryptedData : null;
  //       await this.updateCartItems(cartItems, property, productVariantId, qty);
  //     }
  //   }

  //   eventType === 'connect'
  //     ? await this.updateCartAttribute(window.userWallet)
  //     : await this.updateCartAttribute(null);

  //   // Process product-price elements in the PDP
  //   if (mainProduct) {
  //     const productPriceEls = document.querySelectorAll(
  //       '.bevy-main-product product-price',
  //     );
  //     for (const productPriceEl of productPriceEls) {
  //       const {productVariantId, quantity} = productPriceEl;
  //       const variantId = productPriceEl.dataset.productVariantId;
  //       productPriceEl.discountEligible = false;
  //       productPriceEl.getAndSetPrice();
  //     }
  //   }
  // }

  async handlePricing(eventType) {
    const cartItems = await this.getAllCartItems();
    const cartItemProductPriceEls = document.querySelectorAll(
      '#main-cart-items product-price',
    );

    const mainProduct = document.querySelector('.bevy-main-product');

    let updatePromises = [];

    if (cartItemProductPriceEls.length > 0)
      this.sectionLoader('.cart-contents-wrapper', 'show');

    const eligibleNFTsSet = new Set();

    for (const productPriceEl of cartItemProductPriceEls) {
      const {
        productVariantId,
        quantity,
        requiredTraits,
        requiredCollection,
        productGate,
        productTags,
      } = productPriceEl.dataset;

      const {publicSaleDiscount, walletDiscount} = productPriceEl;

      const qty = quantity || 1;
      const burnTag = productTags.split('|').find((tag) => tag === 'burn'); // return @string "burn"
      productPriceEl.discountEligible = false;
      productPriceEl.getAndSetPrice();
      const encryptionAvailable =
        eventType === 'connect' && (publicSaleDiscount || walletDiscount);

      if (window.location.pathname.startsWith('/cart')) {
        // const wallet_data = {
        //   current_collections: requiredCollection?.split(','),
        //   gate: productGate,
        //   traits_with_collection: JSON.parse(requiredTraits),
        // };
        const token = {
          _DEV: encryptionAvailable,
          _IDFBN: 'none',
          _BIQ: 0,
        };
        const key = window?.custom?.key;
        const encryptedToken = encrypt(JSON.stringify(token), key);

        if (eventType === 'connect') {
          const filteredNFTs = await this.filterEligibleNFTs(
            window.userWallet,
            requiredCollection,
            requiredTraits,
            productGate,
          );
          filteredNFTs.forEach((nft) =>
            eligibleNFTsSet.add(JSON.stringify(nft)),
          );
        }

        const encryptedData = encryptionAvailable ? encryptedToken : null;
        updatePromises.push(
          this.prepareCartItemUpdate(
            cartItems,
            encryptedData,
            productVariantId,
            qty,
          ),
        );
      }
    }

    const itemUpdates = await Promise.all(updatePromises);
    await this.batchUpdateCartItems(itemUpdates);

    // Update global cart attribute if needed
    // await this.updateCartAttribute(
    //   eventType === 'connect' ? [...eligibleNFTsSet].map(JSON.parse) : null,
    // );
    await this.updateCartAttribute(
      eventType === 'connect' ? cartItems.token : null,
    );

    // Process product-price elements in the PDP
    if (mainProduct) {
      const productPriceEls = document.querySelectorAll(
        '.bevy-main-product product-price',
      );
      for (const productPriceEl of productPriceEls) {
        const {productVariantId, quantity} = productPriceEl;
        const variantId = productPriceEl.dataset.productVariantId;
        productPriceEl.discountEligible = false;
        productPriceEl.getAndSetPrice();
      }
    }
    this.sectionLoader('.cart-contents-wrapper', 'hide');
  }

  async prepareCartItemUpdate(cartItems, encryptedData, id, qty = 1) {
    const item = cartItems.items.find((item) => item.id === Number(id));
    if (!item) return null;

    // find the index value of the item in the cartItems
    const index =
      cartItems.items.findIndex((item) => item.id === Number(id)) + 1;

    return {
      id: Number(item.id),
      line: index,
      quantity: qty,
      properties: {
        _wallet_data: encryptedData,
      },
    };
  }

  async batchUpdateCartItems(itemUpdates) {
    try {
      const filteredUpdates = itemUpdates.filter((update) => update !== null);
      if (filteredUpdates.length === 0) return;

      for (const update of filteredUpdates) {
        await this.updateSingleCartItem(update);
      }
    } catch (error) {
      console.error('Error updating cart items:', error);
    }
  }

  async updateSingleCartItem(update) {
    try {
      // console.log(update);
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: update.line,
          quantity: update.quantity,
          properties: update.properties,
        }),
      });

      console.log(`Updating Cart for ${update.id} - ${update.line}:`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error updating item ${update.id}:`, error);
    }
  }

  async filterEligibleNFTs(
    nfts,
    requiredCollections,
    requiredTraits,
    gateType,
  ) {
    const requiredCollectionArray = requiredCollections
      .split(',')
      .map((c) => c.trim());
    const requiredTraitsArray = JSON.parse(requiredTraits);

    return nfts.reduce((acc, nft) => {
      if (gateType === 'token-gate') {
        if (
          requiredCollectionArray.some((collectionAddress) =>
            this.isNFTInCollection(nft, collectionAddress),
          )
        ) {
          acc.push(nft);
        }
      } else if (gateType === 'trait-gate') {
        for (const {
          collection: collectionAddress,
          traits: traitsArray,
        } of requiredTraitsArray) {
          if (this.isNFTInCollection(nft, collectionAddress)) {
            if (this.doesNFTMatchTraits(nft, traitsArray)) {
              acc.push(nft);
            }
            break; // Exit the loop after checking the matching collection
          }
        }
      }
      return acc;
    }, []);
  }

  async updateCartAttribute(walletData) {
    try {
      const formData = new FormData();

      // const modifiedWalletData = walletData
      //   ? await this.processWalletData(walletData)
      //   : null;

      // console.log('⇐-------------↓↓↓ modifiedWalletData ↓↓↓-------------⇒');
      // console.log({ modifiedWalletData});
      // console.log('⇐-------------↑↑↑ modifiedWalletData ↑↑↑-------------⇒');

      formData.append(
        'attributes[_wallet_data]',
        JSON.stringify(walletData),
      );

      const sectionsToRender = this.getSectionsToRender();
      sectionsToRender.forEach((section) => {
        formData.append('sections[]', section.section);
      });
      formData.append('sections_url', window.location.pathname);

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        body: formData,
      });

      const data = await response.text();
      this.rerenderCart(data);
    } catch (error) {
      console.error('Error updating cart attribute:', error);
      // Consider adding more robust error handling here
    }
  }

  // Helper function to check if an NFT is already in the array
  isNFTInArray(nft, nftArray) {
    return nftArray.some((existingNft) => this.isNFTEqual(existingNft, nft));
  }

  // Helper function to compare NFTs based on the provided structure
  isNFTEqual(nft1, nft2) {
    // Compare grouping
    if (nft1.grouping.length !== nft2.grouping.length) return false;
    for (let i = 0; i < nft1.grouping.length; i++) {
      if (
        nft1.grouping[i].group_key !== nft2.grouping[i].group_key ||
        nft1.grouping[i].group_value !== nft2.grouping[i].group_value
      ) {
        return false;
      }
    }

    // Compare content.metadata.attributes
    const attrs1 = nft1.content.metadata.attributes;
    const attrs2 = nft2.content.metadata.attributes;
    if (attrs1.length !== attrs2.length) return false;
    for (let i = 0; i < attrs1.length; i++) {
      if (
        attrs1[i].trait_type !== attrs2[i].trait_type ||
        attrs1[i].value !== attrs2[i].value
      ) {
        return false;
      }
    }

    return true;
  }

  async processWalletData(walletData) {
    if (!Array.isArray(walletData) || walletData.length === 0) {
      return null;
    }

    return walletData.reduce((acc, {content, grouping}) => {
      if (grouping && grouping.length > 0) {
        acc.push({
          content: {
            metadata: {
              attributes: content?.metadata?.attributes,
            },
          },
          grouping,
        });
      }
      return acc;
    }, []);
  }

  async rerenderCart(data) {
    try {
      const parsedJson = JSON.parse(data);

      const sections = this.getSectionsToRender();
      sections.forEach((sectionArr) => {
        const sectionElement = document.getElementById(sectionArr.id);
        if (!sectionElement) {
          console.warn(
            `Element with id "${sectionArr.id}" not found. Skipping update for this section.`,
          );
          return;
        }

        const elementToReplace =
          sectionElement.querySelector(sectionArr.selector) || sectionElement;

        if (!parsedJson.sections || !parsedJson.sections[sectionArr.section]) {
          console.warn(
            `Section "${sectionArr.section}" not found in the response. Skipping update for this section.`,
          );
          return;
        }

        const newHTML = this.getSectionInnerHTML(
          parsedJson.sections[sectionArr.section],
          sectionArr.selector,
        );

        if (newHTML !== null) {
          elementToReplace.innerHTML = newHTML;
        } else {
          console.warn(
            `Failed to extract inner HTML for section "${sectionArr.section}". Skipping update for this section.`,
          );
        }
      });
    } catch (error) {
      console.error('Error re-rendering price section:', error);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '#main-cart-footer .cart-footer-total',
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    if (!html) {
      console.warn('Received empty HTML');
      return null;
    }

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);

    if (!element) {
      console.warn(`Selector "${selector}" not found in the parsed HTML`);
      return null;
    }

    return element.innerHTML;
  }

  // async updateCartItems(cartItems, itemProperties, id, qty = 1) {
  //   // Find the corresponding item in cartItems
  //   const item = cartItems.items.find((item) => item.id === Number(id));

  //   if (itemProperties === undefined || itemProperties === 'undefined') return;

  //   // Find the index of the item in cartItems
  //   const lineIndex = cartItems.items.indexOf(item) + 1;

  //   // Update the item properties in the cart
  //   await fetch(`/cart/change.js`, {
  //     method: 'POST',
  //     headers: {
  //       'Content-Type': 'application/json',
  //     },
  //     body: JSON.stringify({
  //       line: lineIndex,
  //       quantity: qty,
  //       properties: {
  //         _wallet_data: itemProperties,
  //       },
  //       sections: this.getSectionsToRender().map((section) => section.section),
  //       sections_url: window.location.pathname,
  //     }),
  //   })
  //     .then((response) => response.text())
  //     .then((data) => {
  //       this.rerenderCart(data);
  //     });
  // }

  // async rerenderCart(data) {
  //   try {
  //     const parsedJson = JSON.parse(data);

  //     const sections = this.getSectionsToRender();
  //     sections.forEach((sectionArr) => {
  //       const elementToReplace =
  //         document
  //           .getElementById(sectionArr.id)
  //           .querySelector(sectionArr.selector) ||
  //         document.getElementById(section.id);
  //       elementToReplace.innerHTML = this.getSectionInnerHTML(
  //         parsedJson.sections[sectionArr.section],
  //         sectionArr.selector,
  //       );
  //     });
  //   } catch (error) {
  //     console.error('Error re-rendering price section:', error);
  //   }
  // }

  // getSectionsToRender() {
  //   return [
  //     {
  //       id: 'main-cart-items',
  //       section: document.getElementById('main-cart-items')?.dataset.id,
  //       selector: '.js-contents',
  //     },
  //     {
  //       id: 'main-cart-footer',
  //       section: document.getElementById('main-cart-footer')?.dataset.id,
  //       selector: '#main-cart-footer .cart-footer-total',
  //     },
  //   ];
  // }

  // getSectionInnerHTML(html, selector) {
  //   return new DOMParser()
  //     .parseFromString(html, 'text/html')
  //     .querySelector(selector).innerHTML;
  // }

  async updateStrings(eventType) {
    const stringEl = document.querySelector('.cart-footer-string');
    if (stringEl) {
      const cartItems = await this.getAllCartItems();
      if (eventType === 'connect') {
        let cart_discount_eligible = false;
        for (let item of cartItems.items) {
          if (item.discounts.length > 0) {
            cart_discount_eligible = true;
          }
        }
        if (cart_discount_eligible) {
          stringEl.innerText = 'All active discounts have been applied.';
        } else {
          stringEl.innerText =
            'Sorry, there are currently no discounts for the products in your cart.';
        }
      } else if (eventType === 'disconnect') {
        stringEl.innerText =
          'Are you a Claynosaurz Digital Collectible Holder? Connect your wallet to check for a discount.';
      }
    }
  }

  async getAllCartItems() {
    return await fetch(window.Shopify.routes.root + 'cart.js')
      .then((response) => response.json())
      .then((data) => {
        return data;
      });
  }

  sectionLoader(selector, action) {
    const isId = selector.startsWith('#');
    const elements = isId
      ? [document.getElementById(selector.slice(1))]
      : document.querySelectorAll(selector);

    elements.forEach((element) => {
      if (!element) return;

      let overlay = element.querySelector('.section-overlay');

      if (action === 'show') {
        if (!overlay) {
          overlay = this.createOverlay();
          element.style.position = 'relative';
          element.appendChild(overlay);
        }
        overlay.style.display = 'flex';
        element.style.pointerEvents = 'none';
      } else if (action === 'hide') {
        if (overlay) {
          overlay.style.display = 'none';
          element.style.pointerEvents = 'auto';
        }
      }
    });
  }

  createOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'section-overlay';

    const content = document.createElement('div');
    content.className = 'section-overlay__content';

    const spinner = document.createElement('div');
    spinner.className = 'section-overlay__spinner';

    const text = document.createElement('div');
    text.className = 'section-overlay__text';
    text.textContent = 'Loading...';

    content.appendChild(spinner);
    content.appendChild(text);
    overlay.appendChild(content);

    return overlay;
  }
}

customElements.define('web3-provider-wrapper', Web3Provider);

class ProductPrice extends HTMLElement {
  constructor() {
    super();
    this.productVariantId = this.dataset.productVariantId;
    this.productGate = this.dataset.productGate;
    this.publicSaleDiscount =
      this.dataset.publicSaleDiscount === 'true' ? true : false;
    this.requiredTraits = this.dataset.requiredTraits;
    this.requiredCollection = this.dataset.requiredCollection;
    this.nftDiscountValue = this.dataset.nftDiscountValue;
    this.productTags = this.dataset.productTags; // passing tags with | separator.
    this.currencySymbolEnabled = this.dataset.currencySymbolEnabled === 'true';
    this.currencyCodeEnabled = this.dataset.currencyCodeEnabled === 'true';
    this.currencySymbol = this.dataset.currencySymbol || '$';
    this.currencyCode = this.dataset.currencyCode || 'USD';
    this.walletDiscount =
      this.nftDiscountValue !== undefined &&
      this.requiredCollection !== undefined &&
      this.productGate !== undefined &&
      !this.publicSaleDiscount;
    this.price = this.convertToNumber(
      this.dataset.price,
      this.currencySymbol,
      this.currencyCode,
    );
    this.qty = this.dataset.quantity || 1;
    this.encryptedToken = false;
    this.discountType = 'fixedAmount';
    this.lineItemDiscountedPrice = this.dataset.lineItemDiscount;
    this.compareAtPrice = this.convertToNumber(
      this.dataset.compareAtPrice,
      this.currencySymbol,
      this.currencyCode,
    );
    this.getAndSetPrice();
  }

  connectedCallback() {
    try {
      this.bindEvents();
    } catch (error) {
      console.error('An error occurred:', error);
    }
  }

  bindEvents() {
    try {
      document.addEventListener(
        'wallet-connected',
        this.getAndSetPrice.bind(this),
      );
      document.addEventListener(
        'wallet-disconnected',
        this.getAndSetPrice.bind(this),
      );
      document.addEventListener('add-to-cart', this.onAddToCart.bind(this));
      document.addEventListener(
        'remove-from-cart',
        this.onRemoveFromCart.bind(this),
      );
    } catch (error) {
      console.error('error on updating real time:', error);
    }
  }

  async getAndSetPrice() {
    try {
      // checking discount type
      if (this.nftDiscountValue) {
        this.checkDiscountType();
      }

      // generating encrypted token
      if (!this.encryptedToken) {
        await this.generateEncryptedToken();
      }

      // calculating discounted price
      if (this.encryptedToken) {
        this.getDiscountedPrice();
      }

      // updating the price in the DOM
      this.innerHTML = this.getPricingHTML();
    } catch (error) {
      console.error(error);
    }
  }

  async updateStringsOnRemove(eventType) {
    const stringEl = document.querySelector('.cart-footer-string');
    if (stringEl) {
      const cartItems = await this.getAllCartItems();
      if (eventType === 'connect') {
        let cart_discount_eligible = false;
        for (let item of cartItems.items) {
          if (item.properties) {
            if (item.discounts.length > 0) {
              cart_discount_eligible = true;
            }
          }
        }
        if (cart_discount_eligible) {
          stringEl.innerText = 'All active discounts have been applied';
        } else {
          stringEl.innerText =
            'Sorry, there are currently no discounts for the products in your cart.';
        }
      } else if (eventType === 'disconnect') {
        stringEl.innerText =
          'Are you a Claynosaurz Digital Collectible Holder? Connect your wallet to check for a discount.';
      }
    }
  }

  async generateEncryptedToken() {
    try {
      if (localStorage.getItem('walletConnected') === 'false') {
        console.info('Wallet not connected');
        return (this.encryptedToken = false);
      }
      let productGatePassed = false;

      this.wallet = window.userWallet || []; // we will have all NFTs as an array of objects

      const tags = this.productTags.split('|');
      const burnTag = tags.find((tag) => tag === 'burn');

      // check the product gate to see the discount eligibility.
      const web3Provider = document.querySelector('web3-provider-wrapper'); // accessing it to access a function;

      // checking is it the product page or not.
      if (
        web3Provider.querySelector('.bevy-main-product') &&
        this.publicSaleDiscount
      ) {
        productGatePassed = false; // making it false, because client don't need to show discounted price in PDP page.
      } else {
        // checking if public sale discount is enabled then it is eligible for the discount.
        if (this.productGate === 'trait-gate') {
          if (
            this.requiredTraits !== 'undefined' &&
            this.requiredTraits !== ''
          ) {
            productGatePassed = await web3Provider.findAndCheckNFT(
              this.wallet,
              this.requiredCollection,
              this.requiredTraits,
              this.productGate,
            );
          }
        } else if (this.productGate === 'token-gate') {
          // console.log('Checking token gate');
          productGatePassed = await web3Provider.findAndCheckNFT(
            this.wallet,
            this.requiredCollection,
            null,
            this.productGate,
          );
        }
      }

      // if connected wallet has the NFT or the collection, then the discount is eligible
      if (
        productGatePassed &&
        this.nftDiscountValue !== 'undefined' &&
        this.nftDiscountValue !== '' &&
        burnTag !== 'burn'
      ) {
        this.encryptedToken = this.publicSaleDiscount || this.walletDiscount;

        // adding a hidden input field to the product form to pass the discount eligibility to the cart page as properties.
        const productForm = document.querySelector(
          'product-form.product-form form',
        );
        this.addEncryptedTokenInput(
          productForm,
          this.productVariantId,
          this.encryptedToken,
        );
      } else if (productGatePassed && burnTag && burnTag === 'burn') {
        this.encryptedToken = this.publicSaleDiscount || this.walletDiscount;
        // adding a hidden input field to the product form to pass the discount eligibility to the cart page as properties.
        const productForm = document.querySelector(
          'product-form.product-form form',
        );
        this.addEncryptedTokenInput(
          productForm,
          this.productVariantId,
          this.encryptedToken,
        );
      }
      return this.encryptedToken;
    } catch (error) {
      console.error('Error generating encrypted token: ', error);
      return error;
    }
  }

  // checking if the discount is a percentage or fixed amount
  // discount value should be in the format of 10% or $10
  checkDiscountType() {
    try {
      if (this.nftDiscountValue?.includes('%')) {
        this.discountType = 'percentage';
      }
      return this.discountType;
    } catch (error) {
      console.error('Error checking discount type: ', error);
      return error;
    }
  }

  convertToNumber(priceString, currencySymbol, currencyCode) {
    if (currencyCode === 'USD' || currencySymbol === '$') {
      // For USD and similar formats where commas separate thousands and periods are for decimals
      return parseFloat(priceString.replace(/,/g, ''));
    } else {
      // For European formats where periods separate thousands and commas are for decimals
      const formattedPriceString = priceString
        .replace(/\./g, '')
        .replace(',', '.');
      const price = parseFloat(formattedPriceString);

      return price;
    }
  }

  // filtering the discount value and calculating the discounted price
  getDiscountedPrice() {
    try {
      // Input validation
      const price = Number(this.price);
      const qty = Number(this.qty);
      const nftDiscountValue = this.nftDiscountValue;

      if (isNaN(price) || isNaN(qty) || qty <= 0) {
        throw new Error('Invalid price or quantity');
      }

      if (this.discountType === 'percentage') {
        const percentage = 1 - Number(nftDiscountValue.replace('%', '')) / 100;
        this.discountedPrice = (percentage * price).toFixed(2);
      } else if (this.discountType === 'fixedAmount') {
        const productPrice = price * qty;
        const discountValue = Number(nftDiscountValue.replace('$', ''));
        const discountedPrice = productPrice - discountValue;

        if (discountedPrice < 0) {
          throw new Error('Discount exceeds total price');
        }

        this.discountedPrice = (discountedPrice / qty).toFixed(2);
      } else {
        throw new Error('Unknown discount type');
      }

      return this.discountedPrice ?? null;
    } catch (error) {
      console.error('Error getting discount price: ', error.message);
      return null; // or return a default value like the original price
    }
  }

  getTrimmedDecimalZeros(value) {
    return value.toString().replace(/(\.[0-9]*[1-9])0+$|\.0*$/, '$1');
  }

  getFormattedPrice(price) {
    try {
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.currencyCode,
        minimumFractionDigits: 0, // Set minimum fraction digits to 0
        maximumFractionDigits: 2, // Set maximum fraction digits to 2 in case there are fractional values
      }).format(price);

      return this.currencySymbolEnabled
        ? formattedPrice
        : formattedPrice.replace(this.currencySymbol, '').trim();
    } catch (error) {
      console.error('Error formatting price:', error);
      return price; // Fallback to the original price if there's an error
    }
  }

  getPricingHTML() {
    try {
      let pricingHTML = '';
      const compareAtPrice = Number(this.compareAtPrice); // Original compare at price
      const price = Number(this.price); // Original price
      const discountedPrice = Number(this.discountedPrice); // Wallet discount price
      const lineItemDiscountedPrice = Number(this.lineItemDiscountedPrice); // Non-wallet discount price

      // Calculate if each price should be displayed
      const hasCompareAtPrice = compareAtPrice > price; // Is there a compare at price?
      const hasLineItemDiscount =
        lineItemDiscountedPrice > 0 && lineItemDiscountedPrice < price; // Non-wallet discount available
      const hasWalletDiscount =
        this.encryptedToken && discountedPrice > 0 && discountedPrice < price; // Wallet discount available

      // Collect all prices into an array
      let allPrices = [
        {price: price, className: 'selling-price'},
        ...(hasCompareAtPrice
          ? [{price: compareAtPrice, className: 'compare-price'}]
          : []),
        ...(hasLineItemDiscount
          ? [{price: lineItemDiscountedPrice, className: 'non-wallet-discount'}]
          : []),
        ...(hasWalletDiscount
          ? [{price: discountedPrice, className: 'wallet-discount'}]
          : []),
      ];

      // Sort prices from lowest to highest
      allPrices.sort((a, b) => a.price - b.price);

      // Determine the lowest price (best deal)
      const lowestPrice = Math.min(...allPrices.map((p) => p.price));

      // Generate HTML for each price
      const displayedPrices = new Set(); // Track displayed prices to avoid duplicates

      allPrices.forEach(({price, className}) => {
        const isLowestPrice = price === lowestPrice; // Check if it's the best deal
        const isStrikeOut =
          className === 'compare-price' ||
          (className === 'selling-price' &&
            (hasLineItemDiscount || hasWalletDiscount)) ||
          (!isLowestPrice &&
            (className === 'non-wallet-discount' ||
              className === 'wallet-discount'));

        // Check for duplicates based on price
        if (!displayedPrices.has(price)) {
          displayedPrices.add(price);
          pricingHTML += `<span class="price ${className} ${
            isStrikeOut ? 'strike-out' : ''
          }">${this.getFormattedPrice(price)}</span>`;
        }
      });

      return pricingHTML;
    } catch (error) {
      console.error('Error generating Pricing HTML: ', error);
      return `<span class="error">Error displaying price</span>`;
    }
  }

  onAddToCart() {
    this.getAndSetPrice();
    console.info('Item added to cart');
  }

  onRemoveFromCart() {
    this.getAndSetPrice();
    if (localStorage.getItem('walletConnected') === 'true') {
      this.updateStringsOnRemove('connect');
    }
    console.info('Item removed from cart');
  }

  // async getCartItemProperties() {
  //   // if the product doesn't have any assigned NFT then return;

  //   const itemProperties = JSON.stringify({
  //     _discount_eligible: true,
  //     discount_type: this.discountType,
  //     discount_value:
  //       this.discountType === 'fixedAmount'
  //         ? this.nftDiscountValue.replace('$', '')
  //         : this.nftDiscountValue?.replace('%', ''),
  //   });

  //   return itemProperties;
  // }

  addEncryptedTokenInput(productForm, productVariantId, _DEV_TOKEN) {
    if (productForm) {
      const productFormVariantId = productForm.querySelector(
        '.product-variant-id',
      ).value;

      // it is blocking to push this input field to the product form if PDP form variant id !== <product-price> component variant id.
      if (productFormVariantId !== productVariantId) return;

      // in case, if we pushed it already then blocking it to push again.
      const findEncryptedTokenInput =
        productForm.querySelector('#encrypted-token');
      if (findEncryptedTokenInput) return; // return if the input field already exists.

      const inputValue = JSON.stringify({
        current_collections: this.requiredCollection?.split(','),
        gate: this.productGate,
        traits_with_collection: JSON.parse(this.requiredTraits),
      });

      const token = {
        _DEV: _DEV_TOKEN,
        _IDFBN: 'none',
        _BIQ: 0,
      };
      const key = window?.custom?.key;

      const encryptedToken = encrypt(JSON.stringify(token), key);

      const encryptedTokenInput = document.createElement('input');
      encryptedTokenInput.type = 'hidden';
      // properties name should start with "_" like "_wallet_data" below in order to keep the properties hidden in the cart
      encryptedTokenInput.name = 'properties[_wallet_data]';
      encryptedTokenInput.value = encryptedToken;
      encryptedTokenInput.id = 'encrypted-token';

      productForm.appendChild(encryptedTokenInput);
    }
  }

  async getAllCartItems() {
    return await fetch(window.Shopify.routes.root + 'cart.js')
      .then((response) => response.json())
      .then((data) => {
        return data;
      });
  }
}
customElements.define('product-price', ProductPrice);

// add a class into an element when details tag is opened
document
  .querySelector('#Details-menu-drawer-container')
  .addEventListener('click', function () {
    this.closest('.bevy-header').classList.toggle('header-drawer-open');
  });

class BevySliderComponent extends SliderComponent {
  constructor() {
    super();
    this.dotsContainer = null;
  }

  connectedCallback() {
    if (this.slider && this.sliderItems.length > 0) {
      this.initPages();
      this.createDots();
      this.bindEvents();
      this.update();
    }
  }

  bindEvents() {
    if (this.slider) {
      this.slider.addEventListener('scroll', this.update.bind(this));
    }
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }
    this.addEventListener('slideChanged', this.onSlideChange.bind(this));
  }

  createDots() {
    if (!this.slider || this.totalPages <= 1) return;

    this.dotsContainer = document.createElement('div');
    this.dotsContainer.classList.add('slider-dots');
    this.dotsContainer.style.cssText = `
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 10px;
    `;

    for (let i = 0; i < this.totalPages; i++) {
      const dot = document.createElement('span');
      dot.classList.add('slider-dot');
      dot.style.cssText = `
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #ccc;
        margin: 0 4px;
        transition: all 0.3s ease;
        cursor: pointer;
      `;
      dot.addEventListener('click', () => this.goToSlide(i + 1));
      this.dotsContainer.appendChild(dot);
    }

    this.slider.parentNode.insertBefore(
      this.dotsContainer,
      this.slider.nextSibling,
    );
    this.updateDots();
  }

  updateDots() {
    if (!this.dotsContainer) return;

    const dots = this.dotsContainer.querySelectorAll('.slider-dot');
    dots.forEach((dot, index) => {
      if (index === this.currentPage - 1) {
        dot.style.backgroundColor = '#000';
        dot.style.width = '16px';
        dot.style.borderRadius = '4px';
      } else {
        dot.style.backgroundColor = '#ccc';
        dot.style.width = '8px';
        dot.style.borderRadius = '50%';
      }
    });
  }

  onSlideChange(event) {
    this.updateDots();
  }

  goToSlide(slideIndex) {
    if (!this.slider) return;

    const position = (slideIndex - 1) * this.sliderItemOffset;
    this.setSlidePosition(position);
    this.update();
  }

  // Override the update method to include null checks
  update() {
    if (!this.slider || !this.nextButton) return;

    super.update();
    this.updateDots();
  }
}

customElements.define('bevy-slider-component', BevySliderComponent);
